import { Button, Container, Label } from '@playcanvas/pcui';

import { Events } from '../events';

type Picture = {
    id: string,
    name?: string,
    exhibitId?: string,
    capturedAt?: string,
    capturedBy?: string,
    device?: string,
    description?: string,
    tags?: string[],
    validated?: boolean,
    image?: {
        url?: string
    }
};

class EvidencePanel extends Container {
    constructor(events: Events, args = {}) {
        args = {
            ...args,
            id: 'evidence-panel',
            class: 'panel',
            hidden: true
        };

        super(args);

        this.style.top = '50%';
        this.style.transform = 'translate(0, -50%)';
        this.style.right = '102px';
        this.style.width = '320px';
        this.style.maxHeight = '70%';
        this.style.flexDirection = 'column';
        this.style.overflow = 'hidden';

        ['pointerdown', 'pointerup', 'pointermove', 'wheel', 'dblclick'].forEach((eventName) => {
            this.dom.addEventListener(eventName, (event: Event) => event.stopPropagation());
        });

        const header = new Container({
            class: 'panel-header'
        });

        const icon = new Label({
            text: '\uE227',
            class: 'panel-header-icon'
        });

        const label = new Label({
            text: 'Evidence',
            class: 'panel-header-label'
        });

        header.append(icon);
        header.append(label);

        const body = new Container({
            id: 'evidence-panel-body'
        });
        body.style.display = 'flex';
        body.style.flexDirection = 'column';
        body.style.gap = '8px';
        body.style.padding = '8px';
        body.style.overflowY = 'auto';

        const gallery = document.createElement('div');
        gallery.id = 'evidence-panel-gallery';
        gallery.style.display = 'flex';
        gallery.style.flexDirection = 'column';
        gallery.style.gap = '6px';

        const galleryFilter = document.createElement('input');
        galleryFilter.id = 'evidence-panel-gallery-filter';
        galleryFilter.type = 'text';
        galleryFilter.placeholder = 'Search by name, exhibit ID, tags';

        const gallerySummary = document.createElement('div');
        gallerySummary.id = 'evidence-panel-gallery-summary';
        gallerySummary.textContent = '0 / 0';
        gallerySummary.style.fontSize = '11px';
        gallerySummary.style.opacity = '0.8';

        const galleryList = document.createElement('div');
        galleryList.id = 'evidence-panel-gallery-list';
        galleryList.style.display = 'flex';
        galleryList.style.flexDirection = 'column';
        galleryList.style.gap = '6px';
        galleryList.style.maxHeight = '220px';
        galleryList.style.overflowY = 'auto';
        galleryList.dataset.virtualization = 'none';
        galleryList.dataset.renderMode = 'fragment';
        galleryList.dataset.scrollContainer = 'bounded';

        gallery.appendChild(galleryFilter);
        gallery.appendChild(gallerySummary);
        gallery.appendChild(galleryList);

        const emptyState = new Label({
            id: 'evidence-panel-empty-state',
            text: 'Select a photo hotspot or choose from the gallery'
        });

        const content = new Container({
            id: 'evidence-panel-content',
            hidden: true
        });
        content.style.display = 'flex';
        content.style.flexDirection = 'column';
        content.style.gap = '8px';

        const image = document.createElement('img');
        image.id = 'evidence-panel-image';
        image.alt = 'Selected evidence image';
        image.style.width = '100%';
        image.style.height = 'auto';
        image.style.objectFit = 'contain';
        image.style.borderRadius = '4px';
        image.style.display = 'none';

        const imageWrap = new Container({
            id: 'evidence-panel-image-wrap'
        });
        imageWrap.dom.appendChild(image);

        const createField = (title: string, id: string) => {
            const row = new Container({
                id: `evidence-panel-row-${id}`
            });
            row.style.display = 'flex';
            row.style.flexDirection = 'column';
            row.style.gap = '2px';

            const rowTitle = new Label({
                text: title
            });
            rowTitle.style.fontWeight = 'bold';

            const rowValue = new Label({
                id: `evidence-panel-field-${id}`,
                text: '—'
            });

            row.append(rowTitle);
            row.append(rowValue);

            return {
                row,
                value: rowValue
            };
        };

        const exhibit = createField('Exhibit ID', 'exhibit-id');
        const capturedAt = createField('Captured Date', 'captured-at');
        const capturedBy = createField('Captured By', 'captured-by');
        const device = createField('Device', 'device');
        const description = createField('Description', 'description');
        const tags = createField('Tags', 'tags');

        const actions = new Container({
            id: 'evidence-panel-actions'
        });
        actions.style.display = 'flex';
        actions.style.gap = '8px';

        const flyToCameraButton = new Button({
            id: 'evidence-panel-fly-to-camera',
            text: 'Fly to Camera'
        });

        const showInSceneButton = new Button({
            id: 'evidence-panel-show-in-scene',
            text: 'Show in Scene'
        });

        const compareButton = new Button({
            id: 'evidence-panel-compare',
            text: 'Compare'
        });

        actions.append(flyToCameraButton);
        actions.append(showInSceneButton);
        actions.append(compareButton);

        content.append(imageWrap);
        content.append(exhibit.row);
        content.append(capturedAt.row);
        content.append(capturedBy.row);
        content.append(device.row);
        content.append(description.row);
        content.append(tags.row);
        content.append(actions);

        body.dom.appendChild(gallery);
        body.append(emptyState);
        body.append(content);

        this.append(header);
        this.append(body);

        let selectedId: string | null = null;
        let filterText = '';
        let pictures = (events.invoke('pictures.list') as Picture[]) ?? [];

        const displayText = (value: unknown) => {
            if (typeof value !== 'string') {
                return '—';
            }
            const trimmed = value.trim();
            return trimmed.length > 0 ? trimmed : '—';
        };

        const displayTags = (value: unknown) => {
            if (!Array.isArray(value) || value.length === 0) {
                return '—';
            }
            const tagsText = value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0);
            return tagsText.length > 0 ? tagsText.join(', ') : '—';
        };

        const matchesFilter = (picture: Picture) => {
            if (!filterText) {
                return true;
            }

            const searchable = [
                picture.name,
                picture.exhibitId,
                ...(Array.isArray(picture.tags) ? picture.tags : [])
            ].filter((value): value is string => typeof value === 'string').map(value => value.toLowerCase());

            return searchable.some(value => value.includes(filterText));
        };

        const renderGallery = () => {
            const all = Array.isArray(pictures) ? pictures : [];
            const filtered = all.filter(matchesFilter);
            gallerySummary.textContent = `${filtered.length} / ${all.length}`;

            while (galleryList.firstChild) {
                galleryList.removeChild(galleryList.firstChild);
            }

            const fragment = document.createDocumentFragment();

            for (const picture of filtered) {
                const item = document.createElement('div');
                item.dataset.galleryItemId = String(picture.id);
                item.style.display = 'flex';
                item.style.flexDirection = 'column';
                item.style.gap = '4px';
                item.style.padding = '6px';
                item.style.border = '1px solid rgba(255, 255, 255, 0.15)';
                item.style.borderRadius = '4px';
                item.style.background = String(picture.id) === String(selectedId) ? 'rgba(255, 255, 255, 0.08)' : 'transparent';

                const top = document.createElement('div');
                top.style.display = 'flex';
                top.style.gap = '6px';
                top.style.alignItems = 'center';

                const thumb = document.createElement('img');
                thumb.alt = '';
                thumb.width = 48;
                thumb.height = 36;
                thumb.style.objectFit = 'cover';
                thumb.style.borderRadius = '3px';
                thumb.style.background = 'rgba(255, 255, 255, 0.08)';
                if (picture.image?.url) {
                    thumb.src = picture.image.url;
                }

                const textWrap = document.createElement('div');
                textWrap.style.display = 'flex';
                textWrap.style.flexDirection = 'column';
                textWrap.style.gap = '2px';
                textWrap.style.flex = '1';

                const name = document.createElement('div');
                name.textContent = picture.name?.trim() || `Photo ${picture.id}`;
                name.style.fontWeight = 'bold';

                const exhibitText = document.createElement('div');
                exhibitText.textContent = picture.exhibitId?.trim() || 'No Exhibit ID';
                exhibitText.style.opacity = '0.8';
                exhibitText.style.fontSize = '11px';

                const validation = document.createElement('div');
                validation.dataset.galleryValidation = String(picture.id);
                validation.textContent = picture.validated === false ? 'Needs validation' : 'Validated';
                validation.style.fontSize = '11px';
                validation.style.opacity = '0.85';

                textWrap.appendChild(name);
                textWrap.appendChild(exhibitText);
                textWrap.appendChild(validation);

                top.appendChild(thumb);
                top.appendChild(textWrap);

                const actionsRow = document.createElement('div');
                actionsRow.style.display = 'flex';
                actionsRow.style.gap = '6px';

                const selectButton = document.createElement('button');
                selectButton.type = 'button';
                selectButton.dataset.gallerySelect = String(picture.id);
                selectButton.textContent = 'Select';
                selectButton.addEventListener('click', (event) => {
                    event.stopPropagation();
                    events.fire('pictures.select', picture.id);
                });

                const showButton = document.createElement('button');
                showButton.type = 'button';
                showButton.dataset.galleryShowIn3d = String(picture.id);
                showButton.textContent = 'Show in 3D';
                showButton.disabled = picture.validated === false;
                showButton.addEventListener('click', (event) => {
                    event.stopPropagation();
                    events.fire('pictures.select', picture.id);
                    events.fire('pictures.focusSelected');
                });

                actionsRow.appendChild(selectButton);
                actionsRow.appendChild(showButton);

                item.appendChild(top);
                item.appendChild(actionsRow);

                item.addEventListener('click', () => {
                    events.fire('pictures.select', picture.id);
                });

                fragment.appendChild(item);
            }

            galleryList.appendChild(fragment);
        };

        const refresh = () => {
            pictures = (events.invoke('pictures.list') as Picture[]) ?? [];
            const selected = events.invoke('pictures.selected') as Picture | null;

            if (!selected) {
                selectedId = null;
                content.hidden = true;
                emptyState.hidden = false;
                image.style.display = 'none';
                image.removeAttribute('src');
                flyToCameraButton.disabled = true;
                showInSceneButton.disabled = true;
                compareButton.disabled = true;
                renderGallery();
                return;
            }

            selectedId = selected.id;
            content.hidden = false;
            emptyState.hidden = true;
            flyToCameraButton.disabled = false;
            showInSceneButton.disabled = false;
            compareButton.disabled = !selected.image?.url;

            const imageUrl = selected.image?.url;
            if (imageUrl) {
                image.src = imageUrl;
                image.style.display = 'block';
            } else {
                image.style.display = 'none';
                image.removeAttribute('src');
            }

            exhibit.value.text = displayText(selected.exhibitId);
            capturedAt.value.text = displayText(selected.capturedAt);
            capturedBy.value.text = displayText(selected.capturedBy);
            device.value.text = displayText(selected.device);
            description.value.text = displayText(selected.description);
            tags.value.text = displayTags(selected.tags);
            renderGallery();
        };

        const setVisible = (visible: boolean) => {
            if (visible === this.hidden) {
                if (visible) {
                    events.fire('viewPanel.setVisible', false);
                    events.fire('colorPanel.setVisible', false);
                    events.fire('casePanel.setVisible', false);
                }
                this.hidden = !visible;
                events.fire('evidencePanel.visible', visible);
            }
        };

        flyToCameraButton.on('click', () => {
            events.fire('pictures.focusSelected');
        });

        showInSceneButton.on('click', () => {
            if (!selectedId) {
                return;
            }
            events.fire('picture.setEnabled', true);
            events.fire('pictures.cues.setEnabled', true);
            events.fire('pictures.select', selectedId);
            events.fire('pictures.focusSelected');
        });

        compareButton.on('click', () => {
            events.fire('evidenceComparison.open');
        });

        events.on('pictures.selectedChanged', () => {
            refresh();
        });

        events.on('pictures.listChanged', () => {
            refresh();
        });

        events.on('pictures.metadataChanged', (id: string) => {
            if (!selectedId || String(id) !== String(selectedId)) {
                return;
            }
            refresh();
        });

        events.on('pictures.imageChanged', (id: string) => {
            if (selectedId && String(id) === String(selectedId)) {
                refresh();
                return;
            }
            pictures = (events.invoke('pictures.list') as Picture[]) ?? [];
            renderGallery();
        });

        galleryFilter.addEventListener('input', () => {
            filterText = galleryFilter.value.trim().toLowerCase();
            renderGallery();
        });

        events.function('evidencePanel.visible', () => {
            return !this.hidden;
        });

        events.on('evidencePanel.setVisible', (visible: boolean) => {
            setVisible(visible);
        });

        events.on('evidencePanel.toggleVisible', () => {
            setVisible(this.hidden);
        });

        events.on('viewPanel.visible', (visible: boolean) => {
            if (visible) {
                setVisible(false);
            }
        });

        events.on('colorPanel.visible', (visible: boolean) => {
            if (visible) {
                setVisible(false);
            }
        });

        events.on('casePanel.visible', (visible: boolean) => {
            if (visible) {
                setVisible(false);
            }
        });

        refresh();
    }
}

export { EvidencePanel };
